import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  MAX_PRICELIST_CASE_FILE_NOTES,
  type PricelistCaseFileDTO,
} from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { Card } from '@/components/ui/card';
import { NoteComposer } from '@/components/common/comments/NoteComposer';
import { NoteDeleteDialog } from '@/components/common/comments/NoteDeleteDialog';
import { NoteEditForm } from '@/components/common/comments/NoteEditForm';
import { NoteRowActions } from '@/components/common/comments/NoteRowActions';
import { persistNoteChange } from '@/components/common/comments/noteRowPersist';
import { invalidatePricelistCaseFileQueries } from './pricelistCaseFileQueries';
import { normalizePricelistCaseFileNotes } from './pricelistCaseFileRowState';

function FileNoteTimestamp({ at }: { at: Date }) {
  const when = formatDistanceToNow(at, { addSuffix: true });
  return (
    <time
      className="tabular-nums text-[10px] text-muted-foreground"
      dateTime={at.toISOString()}
      title={format(at, 'MMM d, yyyy · h:mm a')}
    >
      {when}
    </time>
  );
}

function SavedFileNote({
  notes,
  notesUpdatedAt,
  canEdit,
  isBusy,
  onUpdate,
  onDelete,
}: {
  notes: string;
  notesUpdatedAt: Date | null;
  canEdit: boolean;
  isBusy: boolean;
  onUpdate: (body: string) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const gt = useGT();
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  async function handleSave(body: string): Promise<void> {
    await persistNoteChange({
      run: () => onUpdate(body.trim()),
      onSuccess: () => setEditing(false),
      successMessage: gt('Note updated', {
        context: 'toast after editing a pricelist case file note',
      }),
      fallbackError: gt('Could not update the note', {
        context: 'toast after editing a pricelist case file note failed',
      }),
    });
  }

  async function handleDelete(): Promise<void> {
    await persistNoteChange({
      run: onDelete,
      onSuccess: () => setDeleteOpen(false),
      successMessage: gt('Note deleted', {
        context: 'toast after deleting a pricelist case file note',
      }),
      fallbackError: gt('Could not delete the note', {
        context: 'toast after deleting a pricelist case file note failed',
      }),
    });
  }

  return (
    <div className="rounded-md bg-stone-50 px-2 py-1.5">
      {editing ? (
        <NoteEditForm
          initialBody={notes}
          isSaving={isBusy}
          maxLength={MAX_PRICELIST_CASE_FILE_NOTES}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="flex items-start justify-between gap-2">
          <p className="mt-0.5 min-w-0 text-xs leading-snug whitespace-pre-wrap text-stone-800">
            {notes}
          </p>
          <span className="flex shrink-0 items-center gap-1">
            {canEdit ? (
              <NoteRowActions
                showEdit
                showDelete
                busy={isBusy}
                editLabel={gt('Edit note', {
                  context:
                    'button that starts editing a pricelist case file note',
                })}
                deleteLabel={gt('Delete note', {
                  context:
                    'button that starts deleting a pricelist case file note',
                })}
                onEdit={() => setEditing(true)}
                onDelete={() => setDeleteOpen(true)}
              />
            ) : null}
            {notesUpdatedAt ? (
              <FileNoteTimestamp at={new Date(notesUpdatedAt)} />
            ) : null}
          </span>
        </div>
      )}
      {canEdit ? (
        <NoteDeleteDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={() => void handleDelete()}
          message={gt('This note will be removed from the file.', {
            context:
              'confirm dialog body when deleting a pricelist case file note',
          })}
          loading={isBusy}
        />
      ) : null}
    </div>
  );
}

export function PricelistCaseFileNote({
  caseId,
  file,
  canEdit,
}: {
  caseId: string;
  file: PricelistCaseFileDTO;
  canEdit: boolean;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const [pendingNotes, setPendingNotes] = useState<
    { notes: string | null; notesUpdatedAt: Date | null } | undefined
  >(undefined);
  const updateMutation = useMutation(
    orpc.inventoryManagement.pricelistCases.updateFile.mutationOptions(),
  );
  const notes = pendingNotes !== undefined ? pendingNotes.notes : file.notes;
  const notesUpdatedAt =
    pendingNotes !== undefined
      ? pendingNotes.notesUpdatedAt
      : file.notesUpdatedAt;
  if (pendingNotes !== undefined && file.notes === pendingNotes.notes) {
    setPendingNotes(undefined);
  }

  async function writeNotes(nextNotes: string | null): Promise<void> {
    await updateMutation.mutateAsync({
      caseId,
      documentId: file.documentId,
      notes: nextNotes,
    });
    setPendingNotes({
      notes: nextNotes,
      notesUpdatedAt: nextNotes === null ? null : new Date(),
    });
    invalidatePricelistCaseFileQueries(queryClient);
  }

  async function handleAdd(body: string): Promise<boolean> {
    let saved = false;
    await persistNoteChange({
      run: () => writeNotes(normalizePricelistCaseFileNotes(body)),
      onSuccess: () => {
        saved = true;
      },
      successMessage: gt('Note saved', {
        context: 'toast after adding a pricelist case file note',
      }),
      fallbackError: gt('Could not save the note', {
        context: 'toast title when saving a pricelist case file note fails',
      }),
    });
    return saved;
  }

  if (notes === null && !canEdit) return null;

  return (
    <Card className="w-1/2 min-w-0 overflow-hidden p-2.5">
      <div className="mb-1.5 flex shrink-0 items-center gap-1.5 text-[11px] font-semibold tracking-[0.06em] text-stone-500 uppercase">
        <T context="heading for the note on a file attached to a pricelist update case">
          File note
        </T>
      </div>
      {notes !== null ? (
        <SavedFileNote
          key={`${file.documentId}-${canEdit}`}
          notes={notes}
          notesUpdatedAt={notesUpdatedAt}
          canEdit={canEdit}
          isBusy={updateMutation.isPending}
          onUpdate={(body) => writeNotes(body)}
          onDelete={() => writeNotes(null)}
        />
      ) : null}
      {canEdit ? (
        <NoteComposer
          className="w-full"
          resizable
          disabled={notes !== null}
          onSubmit={handleAdd}
          isSubmitting={updateMutation.isPending}
        />
      ) : null}
    </Card>
  );
}
